import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import { ArrowLeft, Download, Share2, Printer, AlertTriangle, ClipboardList } from 'lucide-react';
import toast from 'react-hot-toast';

function QRCodeDisplay({ url, size = 220 }) {
  const canvasRef = useRef(null);
  const [imgData, setImgData] = useState('');

  useEffect(() => {
    if (!url) return;
    const loadAndGenerate = () => {
      if (window.QRCode) {
        const div = document.createElement('div');
        new window.QRCode(div, {
          text: url,
          width: size * 2,
          height: size * 2,
          colorDark: '#1e293b',
          colorLight: '#ffffff',
          correctLevel: window.QRCode.CorrectLevel.H,
        });
        setTimeout(() => {
          const canvas = div.querySelector('canvas');
          if (canvas) setImgData(canvas.toDataURL('image/png'));
        }, 300);
      }
    };
    if (window.QRCode) {
      loadAndGenerate();
    } else {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
      s.onload = loadAndGenerate;
      document.head.appendChild(s);
    }
  }, [url, size]);

  if (!imgData) return (
    <div style={{
      width: size, height: size,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc', borderRadius: 12,
      border: '1px solid var(--border)',
    }}>
      <div className="spinner" />
    </div>
  );

  return (
    <img
      src={imgData}
      alt="QR-Code"
      style={{
        width: size, height: size,
        borderRadius: 12,
        border: '2px solid var(--border)',
        display: 'block',
      }}
    />
  );
}

export default function FahrzeugQR() {
  const { vehicleId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeQR, setActiveQR] = useState('schaden'); // 'schaden' | 'packliste'

  const schadenUrl    = `${window.location.origin}/schaden/qr/${vehicleId}`;
  const packlisteUrl  = `${window.location.origin}/packliste`;

  // URL-Modus erkennen
  useEffect(() => {
    if (location.pathname.includes('/qr/packliste')) setActiveQR('packliste');
    else setActiveQR('schaden');
  }, [location.pathname]);

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`)
      .then(r => setVehicle(r.data))
      .catch(() => toast.error('Fahrzeug nicht gefunden'))
      .finally(() => setLoading(false));
  }, [vehicleId]);

  const currentUrl = activeQR === 'schaden' ? schadenUrl : packlisteUrl;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${activeQR === 'schaden' ? 'Schadensmeldung' : 'Packliste'} — ${vehicle?.license_plate}`,
          url: currentUrl,
        });
      } catch {}
    } else {
      navigator.clipboard?.writeText(currentUrl);
      toast.success('Link kopiert');
    }
  };

  const download = () => {
    const canvas = document.querySelector('img[alt="QR-Code"]');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.src;
    a.download = `QR-${vehicle?.license_plate}-${activeQR}.png`;
    a.click();
  };

  const print = () => window.print();

  if (loading) return (
    <Layout title="QR-Code">
      <div className="loading-box"><div className="spinner spinner-lg"/></div>
    </Layout>
  );

  return (
    <Layout title="QR-Code">
      {/* Back Button */}
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginBottom: 16, paddingLeft: 4 }}
        onClick={() => nav(-1)}>
        <ArrowLeft size={16}/> Zurück
      </button>

      {/* Fahrzeug Info */}
      {vehicle && (
        <div className="card" style={{ marginBottom: 14, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: 'var(--accent)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '1.3rem', flexShrink: 0,
            }}>🚗</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.95rem' }}>
                {vehicle.brand && vehicle.brand !== 'Andere' ? `${vehicle.brand} ${vehicle.model}` : vehicle.model}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {vehicle.license_plate}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Auswahl */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        <button
          className={`btn ${activeQR === 'schaden' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '12px 8px', flexDirection: 'column', gap: 4, height: 'auto' }}
          onClick={() => setActiveQR('schaden')}>
          <AlertTriangle size={20}/>
          <span style={{ fontSize: '.78rem' }}>Schadensmeldung</span>
        </button>
        <button
          className={`btn ${activeQR === 'packliste' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '12px 8px', flexDirection: 'column', gap: 4, height: 'auto' }}
          onClick={() => setActiveQR('packliste')}>
          <ClipboardList size={20}/>
          <span style={{ fontSize: '.78rem' }}>Packliste</span>
        </button>
      </div>

      {/* QR Code */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {/* Label */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 14px', background: 'var(--accent-bg)',
            borderRadius: 999, border: '1px solid rgba(230,57,70,.2)',
          }}>
            {activeQR === 'schaden'
              ? <AlertTriangle size={14} color="var(--accent)"/>
              : <ClipboardList size={14} color="var(--accent)"/>
            }
            <span style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--accent)' }}>
              {activeQR === 'schaden' ? 'Schadensmeldung' : 'Packliste'}
            </span>
          </div>

          {/* QR Code Bild */}
          <div style={{
            padding: 16,
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0,0,0,.08)',
          }}>
            <QRCodeDisplay url={currentUrl} size={220}/>
          </div>

          {/* URL */}
          <div style={{
            padding: '8px 12px',
            background: 'var(--bg)',
            borderRadius: 8,
            width: '100%',
            fontSize: '.72rem',
            color: 'var(--text-muted)',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            textAlign: 'center',
          }}>
            {currentUrl}
          </div>

          {/* Beschreibung */}
          <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6, maxWidth: 280 }}>
            {activeQR === 'schaden'
              ? 'QR-Code scannen um einen Schaden direkt für dieses Fahrzeug zu melden.'
              : 'QR-Code scannen um zur Packlisten-Übersicht zu gelangen.'
            }
          </p>
        </div>
      </div>

      {/* Aktionen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        <button className="btn btn-secondary" style={{ flexDirection: 'column', gap: 4, padding: '12px 8px', height: 'auto' }} onClick={share}>
          <Share2 size={18}/>
          <span style={{ fontSize: '.72rem' }}>Teilen</span>
        </button>
        <button className="btn btn-secondary" style={{ flexDirection: 'column', gap: 4, padding: '12px 8px', height: 'auto' }} onClick={download}>
          <Download size={18}/>
          <span style={{ fontSize: '.72rem' }}>Speichern</span>
        </button>
        <button className="btn btn-secondary" style={{ flexDirection: 'column', gap: 4, padding: '12px 8px', height: 'auto' }} onClick={print}>
          <Printer size={18}/>
          <span style={{ fontSize: '.72rem' }}>Drucken</span>
        </button>
      </div>

      {/* Hinweis */}
      <div style={{
        padding: '12px 14px',
        background: 'var(--blue-bg)',
        borderRadius: 10,
        fontSize: '.78rem',
        color: 'var(--blue)',
        lineHeight: 1.6,
        border: '1px solid rgba(37,99,235,.15)',
      }}>
        <strong>💡 Tipp:</strong> QR-Code ausdrucken und am Fahrzeug befestigen, damit Mitarbeiter schnell Schäden melden können.
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .sidebar, .topbar, .bottom-nav, button, .btn { display: none !important; }
          .card { box-shadow: none !important; border: 1px solid #ccc !important; }
          body { background: white !important; }
        }
      `}</style>
    </Layout>
  );
}
